import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, Mail, Phone, RefreshCw } from "lucide-react";
import { api, API } from "../lib/api";

const TOKEN_KEY = "rbica_admin_token";

const TABS = [
  { key: "discovery_calls", label: "Discovery Calls", path: "/admin/discovery-calls" },
  { key: "contacts", label: "Contact Messages", path: "/admin/contacts" },
  { key: "bookings", label: "Bookings", path: "/admin/bookings" },
  { key: "enrollments", label: "Enrollments", path: "/admin/enrollments" },
  { key: "newsletter", label: "Newsletter", path: "/admin/newsletter" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const LoginCard = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
      toast.success("Welcome back, Reza.");
    } catch (err) {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-radial-sky flex items-center justify-center p-6">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={submit}
        data-testid="admin-login-form"
        className="bg-white p-10 md:p-12 w-full max-w-md shadow-[0_24px_60px_rgba(11,43,64,0.12)] border border-slate-100"
      >
        <div className="text-xs tracking-[0.3em] uppercase text-[#1D4E89] font-medium mb-5">
          RBICA Admin
        </div>
        <h1 className="font-serif text-3xl text-[#0B2B40] tracking-tight leading-tight font-light">
          Welcome back.
        </h1>
        <p className="mt-4 text-[#4A5568] text-sm">
          Enter your password to view enquiries, bookings, and enrollments.
        </p>

        <div className="mt-8">
          <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Password</label>
          <input
            data-testid="admin-password-input"
            type="password"
            className="input-underline"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="mt-10 w-full bg-[#0B2B40] text-white hover:bg-[#1D4E89] disabled:opacity-60 px-9 py-4 text-sm font-medium tracking-wide transition-colors duration-300"
        >
          {loading ? "Verifying..." : "Enter Dashboard"}
        </button>
      </motion.form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [active, setActive] = useState("discovery_calls");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSummary = async () => {
    try {
      const res = await api.get("/admin/summary", { headers });
      setSummary(res.data.counts || {});
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    }
  };

  const fetchTab = async (key) => {
    const tab = TABS.find((t) => t.key === key);
    if (!tab) return;
    setLoading(true);
    try {
      const res = await api.get(tab.path, { headers });
      setData((d) => ({ ...d, [key]: res.data }));
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Could not load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchTab(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTab(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const rows = data[active] || [];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="w-10 h-10 rounded-full bg-[#0B2B40] text-white flex items-center justify-center font-serif">
              RB
            </span>
            <div>
              <div className="font-serif text-lg text-[#0B2B40] leading-tight">
                RBICA Admin Dashboard
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#4A5568]">
                Internal · Reza
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { fetchSummary(); fetchTab(active); }}
              data-testid="admin-refresh-button"
              className="text-[#4A5568] hover:text-[#0B2B40] p-2 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#0B2B40] transition-colors"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-testid={`summary-card-${t.key}`}
              className={`text-left p-5 border transition-all ${
                active === t.key
                  ? "border-[#0B2B40] bg-white shadow-[0_12px_30px_rgba(11,43,64,0.08)]"
                  : "border-slate-100 bg-white hover:border-[#1D4E89]"
              }`}
            >
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#4A5568]">
                {t.label}
              </div>
              <div className="font-serif text-3xl text-[#0B2B40] mt-2">
                {summary[t.key] ?? "—"}
              </div>
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active === t.key
                  ? "border-[#0B2B40] text-[#0B2B40]"
                  : "border-transparent text-[#4A5568] hover:text-[#0B2B40]"
              }`}
            >
              {t.label}{" "}
              <span className="text-xs text-[#4A5568] ml-1">
                ({summary[t.key] ?? 0})
              </span>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-100">
          {loading ? (
            <div className="p-14 text-center text-[#4A5568]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-14 text-center text-[#4A5568]">
              No submissions yet for this section.
            </div>
          ) : (
            <SubmissionList type={active} rows={rows} />
          )}
        </div>
      </main>
    </div>
  );
};

const SubmissionList = ({ type, rows }) => {
  return (
    <ul data-testid={`submission-list-${type}`} className="divide-y divide-slate-100">
      {rows.map((r) => (
        <li key={r.id} className="p-6 hover:bg-[#F8F9FA] transition-colors">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-serif text-lg text-[#0B2B40]">
                  {r.name || r.email}
                </div>
                {r.status && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#E8F0F8] text-[#1D4E89] rounded">
                    {r.status}
                  </span>
                )}
                {r.session_type && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#E8F0F8] text-[#1D4E89] rounded">
                    {r.session_type}
                  </span>
                )}
                {r.delivery_mode && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#E8F0F8] text-[#1D4E89] rounded">
                    {r.delivery_mode}
                  </span>
                )}
              </div>

              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#4A5568]">
                {r.email && (
                  <a
                    href={`mailto:${r.email}`}
                    className="inline-flex items-center gap-1.5 hover:text-[#0B2B40]"
                  >
                    <Mail size={13} /> {r.email}
                  </a>
                )}
                {r.phone && (
                  <a
                    href={`tel:${r.phone}`}
                    className="inline-flex items-center gap-1.5 hover:text-[#0B2B40]"
                  >
                    <Phone size={13} /> {r.phone}
                  </a>
                )}
              </div>

              {r.program_name && (
                <div className="mt-2 text-sm text-[#1A202C]">
                  <span className="text-[#4A5568]">Program:</span>{" "}
                  {r.program_name}
                </div>
              )}
              {r.subject && (
                <div className="mt-2 text-sm text-[#1A202C]">
                  <span className="text-[#4A5568]">Subject:</span>{" "}
                  {r.subject}
                </div>
              )}
              {r.coaching_interest && (
                <div className="mt-2 text-sm text-[#1A202C]">
                  <span className="text-[#4A5568]">Interest:</span>{" "}
                  {r.coaching_interest}
                </div>
              )}
              {(r.preferred_date || r.preferred_time) && (
                <div className="mt-2 text-sm text-[#1A202C]">
                  <span className="text-[#4A5568]">Preferred:</span>{" "}
                  {r.preferred_date} {r.preferred_time && `· ${r.preferred_time}`}
                </div>
              )}
              {r.message && (
                <p className="mt-3 text-sm text-[#1A202C] bg-[#F8F9FA] p-3 border-l-2 border-[#1D4E89] whitespace-pre-wrap">
                  {r.message}
                </p>
              )}
              {r.notes && (
                <p className="mt-3 text-sm text-[#1A202C] bg-[#F8F9FA] p-3 border-l-2 border-[#1D4E89] whitespace-pre-wrap">
                  {r.notes}
                </p>
              )}
              {r.motivation && (
                <p className="mt-3 text-sm text-[#1A202C] bg-[#F8F9FA] p-3 border-l-2 border-[#1D4E89] whitespace-pre-wrap">
                  {r.motivation}
                </p>
              )}
            </div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#4A5568] whitespace-nowrap">
              {formatDate(r.created_at)}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [verifying, setVerifying] = useState(!!token);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    api
      .get("/admin/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setVerifying(false))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setVerifying(false);
      });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#4A5568]">
        Verifying session…
      </div>
    );
  }

  if (!token) return <LoginCard onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
